import { Injectable } from '@angular/core';
import { ViewElement, ViewElementCollection } from '../view';
import { DgControlType } from '../../components/dg-control-type';
import { DomDocumentService } from './dom-document.service';

@Injectable({
  providedIn: 'root'
})
export class ViewElementService {

  public domDgMap = new Map<string, any>();

  private elementMapByType = new Map<string, ViewElement[]>();

  private elementMapByComponent = new Map<string, ViewElement>();

  constructor(private domDocument: DomDocumentService) { }

  public parseMetadataContent(metadataContent: any): ViewElementCollection {
    if (!metadataContent) {
      return null;
    }
    this.domDocument.loadDocument(metadataContent);
    const rootDesignElement = this.createViewElement(metadataContent.module.components[0], null);
    const rootDesignElementCollection = new ViewElementCollection([rootDesignElement], rootDesignElement);
    return rootDesignElementCollection;
  }
  /**
   * 由DOM元素集合创建设计时元素集合
   * @param domElements DOM元素集合
   * @param parent 所属父元素
   */
  private createViewElementCollection(domElements: any[], parent: ViewElement): ViewElementCollection {
    domElements = domElements || [];
    const viewElements: ViewElement[] = [];
    domElements.forEach(domElement => {
      const dgElement = this.createViewElement(domElement, parent);
      viewElements.push(dgElement);
    });
    const designElementCollection = new ViewElementCollection(viewElements, parent);
    const parentDesignElementSymbol = Symbol.for('parent design element');
    designElementCollection[parentDesignElementSymbol] = parent;
    this.bindDesignElementAndDOM(designElementCollection, domElements);
    return designElementCollection;
  }
  /**
   * 由DOM元素创建设计时元素
   * @param domElement DOM元素
   * @param parent 所属父组件
   */
  public createViewElement(domElement: any, parent: ViewElement): ViewElement {
    let componentId = parent ? parent.componentId : '';
    let viewModelId = parent ? parent.viewModelId : '';
    if (domElement.type === 'Component') {
      componentId = domElement.id;
      viewModelId = domElement.viewModel;
    }

    const viewElement = new ViewElement(parent);

    // 从DOM中提取组件属性和子元素集合
    let { contents, ...designElement } = domElement;
    // 如果当前DOM节点为“组件引用”节点，则根据引用关系查找实际组件DOM节点。
    if (designElement.type === 'ComponentRef') {
      const refDomElement = domElement;
      // 将 componentRef 替换为真正的组件节点
      domElement = this.domDocument.getComponentById(designElement.component);
      if (domElement) {
        // 更新DOM结束属性和DOM子节点属性为实际组件DOM属性。
        const { contents: componentContents, ...componentInfo } = domElement;
        designElement = componentInfo;
        contents = componentContents;
        designElement.refElement = refDomElement;
        // 更新当前组件标识和视图模型标识
        componentId = designElement.id;
        viewModelId = designElement.viewModel;
      }
    }
    // 更新设计时组件所属父组件id
    designElement.componentId = componentId;
    // 更新设计时组件所属视图模型id
    designElement.viewModelId = viewModelId;
    // 处理侧边栏等具有Toolbar的DOM节点
    if (designElement.type === 'Sidebar') {
      // 提取sidebar的toolbar
      const toolbar = designElement.toolbar;
      if (toolbar) {
        // designElement.toolbarCollection = new ViewElementCollection([toolbar], designElement);
        designElement.toolbarCollection = this.createViewElementCollection([toolbar], designElement);
      }
    }
    // 处理向导组件等具有header的DOM节点
    if (designElement.type === DgControlType.Wizard) {
      // 提取向导组件的header
      const header = designElement.header;
      if (header) {
        designElement.headerCollection = this.createViewElementCollection(header.contents, designElement);
      }
    }
    // 处理列表组件的列节点
    if (designElement.type === DgControlType.DataGrid || designElement.type === DgControlType.TreeGrid) {
      designElement.fields.forEach(field => {
        this.domDgMap.set(field.id, field);
      });
    }
    // 如果当前DOM节点下有子节点，递归创建设计时组件
    if (contents) {
      designElement.contents = this.createViewElementCollection(contents, designElement);
    } else if (designElement.type === DgControlType.DataGrid) {
      // designElement.fieldCollection = this.createDesignElementCollection(designElement.fields, designElement);
    }

    // 更新设计时组件交互事件
    // designElement.elementFocused = new Subject<any>();
    // designElement.elementChanged = new Subject<any>();
    // designElement.titleChanged = new Subject<any>();
    // 更新设计时组件标识与DOM元素映射字典
    this.domDgMap.set(designElement.id, domElement);
    // designElement.parent = parent;
    Object.assign(viewElement, designElement);
    this.attacheElementToTypeMap(viewElement);
    return viewElement;
  }

  public getElementByType(viewElementType: string): ViewElement[] {
    if (this.elementMapByType.has(viewElementType)) {
      const specialTypeViewElementArray = this.elementMapByType.get(viewElementType);
      return specialTypeViewElementArray;
    }
    return [];
  }

  public getElementByComponentId(componentId: string): ViewElementCollection {
    const componentViewElement = this.elementMapByComponent.get(componentId);
    const result = new ViewElementCollection([componentViewElement], componentViewElement);
    return result;
  }

  private bindDesignElementAndDOM(elementCollection: ViewElementCollection, domElements: any[]) {
    elementCollection.elementIndexChanged.subscribe(({ swappingElement, targetElement }) => {
      const swappingElementIndex = domElements.findIndex(node =>
        node.type === 'ComponentRef' ? node.component === swappingElement.id : node.id === swappingElement.id
      );
      const targetElementIndex = domElements.findIndex(node =>
        node.type === 'ComponentRef' ? node.component === targetElement.id : node.id === targetElement.id
      );
      const swappingDomNode = domElements[swappingElementIndex];
      const targetDomNode = domElements[targetElementIndex];
      if (swappingElementIndex > -1 && targetElementIndex > -1) {
        domElements[swappingElementIndex] = targetDomNode;
        domElements[targetElementIndex] = swappingDomNode;
      }
    });
    elementCollection.elementInserted.subscribe(({ index, element }) => {
      let domElement: any;
      if (element.type === 'Component' && element.refElement) {
        domElement = element.refElement;
      } else {
        domElement = this.domDgMap.get(element.id);
      }
      domElements.splice(index, 0, domElement);
    });
    elementCollection.elementRemoved.subscribe((index) => {
      domElements.splice(index, 1);
    });
  }

  private attacheElementToTypeMap(viewElement: ViewElement) {
    const viewElementType = viewElement.type;
    if (!this.elementMapByType.has(viewElementType)) {
      this.elementMapByType.set(viewElementType, []);
    }
    if (viewElementType === 'Component') {
      this.elementMapByComponent.set(viewElement.id, viewElement);
    }
    const specialTypeViewElementArray = this.elementMapByType.get(viewElementType);
    if (specialTypeViewElementArray.findIndex(element => element.id === viewElement.id) < 0) {
      specialTypeViewElementArray.push(viewElement);
    }
  }
}
